/**
 * Persona-agent finding severity — the single definition, for every layer.
 *
 * The scale is SEV1-style: **1 = critical, 5 = low. Lower is worse.** That inversion is
 * the whole reason this file exists. It had been restated in prose in the zod schema, the
 * runner's tool description, the clusterer and two UI copies; they drifted, and the
 * runner ended up telling the agent the exact opposite ("1 (minor) .. 5 (severe)") while
 * the clusterer ranked trivial nitpicks above blockers.
 *
 * So: nobody open-codes the direction. Don't write `Math.max` to find the worst finding
 * (it returns the mildest) and don't write `6 - severity` at a call site — call
 * `worstSeverity()` and `severityUrgency()`. Anything that weights, sorts, or explains
 * severity derives from here.
 *
 * Consumed by the backend schema + analyst (Backend/functions/src/agents), the persona
 * runner (Backend/e2e-tests/persona-agents) and the admin console.
 * See docs/FeatureDesigns/persona-agents.md.
 */

export const AGENT_SEVERITIES = [1, 2, 3, 4, 5] as const;
export type AgentSeverity = (typeof AGENT_SEVERITIES)[number];

/** Most urgent end of the scale. */
export const MOST_SEVERE: AgentSeverity = 1;
/** Least urgent end of the scale. */
export const LEAST_SEVERE: AgentSeverity = 5;

export const AGENT_SEVERITY_LABELS: Record<AgentSeverity, string> = {
    1: 'critical',
    2: 'major',
    3: 'moderate',
    4: 'minor',
    5: 'low',
};

/** The scale spelled out — for the LLM that reports findings, and for humans reading it. */
export const AGENT_SEVERITY_DESCRIPTION =
    '1 = critical (blocker / broken / data loss), 2 = major, 3 = moderate, 4 = minor, '
    + '5 = low (trivial nitpick). 1 is the MOST severe and 5 the LEAST — lower is more urgent.';

/**
 * Urgency weight — the ONLY place the scale is inverted. Turns a severity into a number
 * that behaves the way arithmetic expects (bigger = more urgent), so callers can weight
 * and rank without thinking about the direction: 1 (critical) → 5 … 5 (low) → 1.
 *
 * Accepts a fractional severity (e.g. a cluster's mean).
 */
export function severityUrgency(severity: number): number {
    return MOST_SEVERE + LEAST_SEVERE - severity;
}

/** The most urgent severity in a set — the LOWEST number, since 1 = critical. */
export function worstSeverity(severities: number[]): number | undefined {
    return severities.length ? Math.min(...severities) : undefined;
}

/** Comparator ordering findings most-urgent-first. */
export function byMostUrgent(a: { severity: number }, b: { severity: number }): number {
    return a.severity - b.severity;
}
